/* @jsxImportSource @emotion/react */

import React from 'react'
import classNames from 'classnames'

import { SearchIcon } from '@slite/icons'

import { NAVBAR_HEIGHT } from './Navbar/NavbarSection'

/** Sticky row of `FilterPill`s with a search field on the right. */
export function FilterBar(props: {
  background: 'moon-dust' | 'moon-dust-lighter'
  searchQuery: string
  onSearchQueryChange: (query: string) => void
  searchLabel: string
  searchPlaceholder: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={classNames(
        'flexbox flexbox-align-center flexbox-space-between flexbox-row-gap-16 flexbox-column-gap-16 flexbox-wrap position-sticky padding-vertical-12',
        `background-${props.background}`,
        props.className
      )}
      css={{ top: NAVBAR_HEIGHT, zIndex: 10 }}
    >
      <div className="flexbox flexbox-align-center flexbox-wrap flexbox-column-gap-4 flexbox-row-gap-4">
        {props.children}
      </div>
      <div className="position-relative width-full" css={{ maxWidth: 280 }}>
        <input
          aria-label={props.searchLabel}
          type="text"
          placeholder={props.searchPlaceholder}
          value={props.searchQuery}
          onChange={(event) => props.onSearchQueryChange(event.target.value)}
          className={classNames(
            'width-full border-small radius-largest padding-vertical-12 padding-left-20 font-size-6',
            props.searchQuery
              ? 'background-white border-shade-05'
              : 'background-transparent border-alpha-shade-40'
          )}
          css={{ paddingRight: 46 }}
        />
        <SearchIcon
          size={18}
          className="position-absolute right-20 shade-30"
          css={{ top: '50%', transform: 'translateY(-50%)' }}
        />
      </div>
    </div>
  )
}

// Set explicitly so a <button> pill matches an <a> pill, which inherits it.
const pillCss = { lineHeight: 1.5 } as const

type FilterPillProps = {
  isActive: boolean
  children: React.ReactNode
} & ({ href: string } | { onClick: () => void })

export function FilterPill(props: FilterPillProps) {
  const className = classNames(
    'text-align-left font-size-6 font-family-system padding-vertical-8 padding-horizontal-16 radius-largest pointer transition font-weight-semibold border-none',
    props.isActive
      ? 'background-moon-dust-darker shade-05 shade-05-on-hover'
      : 'background-transparent shade-10 shade-20-on-hover'
  )

  if ('href' in props) {
    return (
      <a href={props.href} className={className} css={pillCss}>
        {props.children}
      </a>
    )
  }
  return (
    <button
      type="button"
      aria-pressed={props.isActive}
      onClick={props.onClick}
      className={className}
      css={pillCss}
    >
      {props.children}
    </button>
  )
}
